import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
  Chip, Badge, Button, IconButton, Tooltip, TooltipTrigger, TooltipContent, Loader,
} from "@kuda/ui";
import { PencilIcon, TrashIcon, ClockIcon, CopyIcon, TagIcon } from "@kuda/ui/icons";

import Loading from "../components/loading";
import Breadcrumbs from "../components/breadcrumbs";
import Dialogbox from "../components/dialogs/dialogBox";
import RenameSecretDialog from "../components/dialogs/renameSecretDialog";
import DuplicateSecretDialog from "../components/dialogs/duplicateSecretDialog";
import CodeEditor from "../components/monacoEditor";
import SecretDetailsCard from "../components/lastUpdatedby";
import TimeFormatter from "../components/dialogs/timeFormater";
import AwsProfileSelector from "../components/AwsProfileSelector";

import { useSecrets } from "../components/hooks/useSecrets";
import { useSecretActions } from "../components/hooks/useSecretActions";
import { useApiHeaders } from "../components/hooks/useApiHeaders";
import { buildApiHeaders } from "../components/utils/apiHeaders";
import { getTagValue } from "../components/utils/tagHelpers";
import { config, getClusterProviders } from "../components/config";
import useAccessToken from "../auth/accessPermission";
import useUserAccount from "../components/hooks/userAccountInfo";

// Vault uses small integers (1, 2, 3...); AWS/Azure use long version IDs —
// shorten those for display without touching the actual value used to roll back.
const formatVersionLabel = (version) => {
  if (typeof version === "string" && version.length > 12) {
    return `${version.slice(0, 8)}…`;
  }
  return version;
};

function ViewSecret() {
  // "*" not "secretName" — the route is a splat (/view-secret/*) so nested Vault
  // paths like dev/kuda-internaltools/foo survive intact instead of being cut at
  // the first slash.
  const { "*": secretName } = useParams();
  const location       = useLocation();
  const navigate       = useNavigate();
  const searchParams   = new URLSearchParams(location.search);
  const { instance }   = useMsal();

  // URL parameters
  const selectedCluster  = searchParams.get("selectedCluster");
  const platformNameParam = searchParams.get("platformName");
  const vaultNameParam   = searchParams.get("vaultName");
  const awsProfileParam  = searchParams.get("awsProfile");

  // State
  const [secret, setSecret]                   = useState("");
  const [secretTag, setSecretTag]             = useState([]);
  const [updatedOn, setUpdatedOn]             = useState("");
  const [version, setVersion]                 = useState("current");
  const [openSuccess, setOpenSuccess]         = useState(false);
  const [openWarning, setOpenWarning]         = useState(false);
  const [openError, setOpenError]             = useState(false);
  const [rollbackMessage, setRollbackMessage] = useState("");
  const [errorMessage, setErrorMessage]       = useState("");

  // Version history drawer
  const [historyOpen, setHistoryOpen]         = useState(false);
  const [versionHistory, setVersionHistory]   = useState([]);
  const [historyLoading, setHistoryLoading]   = useState(false);

  // Recent viewers — best-effort, see backend/access_log.py for limitations
  const [recentViewers, setRecentViewers]     = useState([]);

  // Duplicate / rename
  const [duplicateOpen, setDuplicateOpen]             = useState(false);
  const [duplicateSuccess, setDuplicateSuccess]       = useState(null); // { newName, targetCluster, targetProvider }
  const [renameOpen, setRenameOpen]                   = useState(false);
  const [renameLoading, setRenameLoading]             = useState(false);
  const [renamePartialOpen, setRenamePartialOpen]     = useState(false);
  const [renamePartialMessage, setRenamePartialMessage] = useState("");

  // AWS profile
  const [awsProfile, setAwsProfile] = useState(() => {
    if (awsProfileParam && config.awsProfiles[awsProfileParam]) return awsProfileParam;
    return "profile-a";
  });

  const [currentVaultId, setCurrentVaultId] = useState(() => {
    if (platformNameParam === config.platforms[0] && config.awsProfiles[awsProfile])
      return config.awsProfiles[awsProfile].region;
    return vaultNameParam;
  });

  const accessToken = useAccessToken([config.apiAccess]);
  const { roles, nameParts } = useUserAccount(instance);

  // Build display name from nameParts for the X-User-Display-Name header
  const userDisplayName = nameParts ? nameParts.join(" ") : "";

  const { loading, fetchSecret, deleteSecret, rollbackSecret, fetchVersionHistory, fetchAccessLog } = useSecrets();
  const { duplicateSecret, renameSecret } = useSecretActions();

  // Use getClusterProviders for the fallback so it works with array-of-arrays format
  const _firstProvider = getClusterProviders(selectedCluster)[0] || { platformName: "aws", vaultId: "" };
  const platformName   = platformNameParam || _firstProvider.platformName;
  const vaultId        = currentVaultId    || _firstProvider.vaultId;

  const headers = useApiHeaders(accessToken, roles, platformName, vaultId, awsProfile, userDisplayName);
  const headersRef = useRef(headers);
  useEffect(() => { headersRef.current = headers; }, [headers]);

  useEffect(() => {
    if (platformName === config.platforms[0] && config.awsProfiles[awsProfile]) {
      setCurrentVaultId(config.awsProfiles[awsProfile].region);
    }
  }, [platformName, awsProfile]);

  const handleAwsProfileChange = (profile) => {
    setAwsProfile(profile);
    if (platformName === config.platforms[0] && config.awsProfiles[profile]) {
      setCurrentVaultId(config.awsProfiles[profile].region);
    }
    const newParams = new URLSearchParams(location.search);
    newParams.set("awsProfile", profile);
    newParams.set("vaultName", config.awsProfiles[profile].region);
    navigate(`${location.pathname}?${newParams.toString()}`, { replace: true });
  };

  // Fetch secret value
  const loadSecret = useCallback(async () => {
    if (!headersRef.current || !accessToken || roles.length === 0) return;
    try {
      const response   = await fetchSecret(secretName, platformName, headersRef.current, version);
      const secretData = response.secretValue;
      const formatted  = secretData["appsettings.json"]
        ? JSON.stringify(secretData["appsettings.json"], null, 2)
        : JSON.stringify(secretData, null, 2);
      setSecret(formatted);
      setSecretTag(response.tags);
      setUpdatedOn(response.updated_on);

      // Best-effort — a failure here shouldn't block viewing the secret itself.
      fetchAccessLog(secretName, platformName, headersRef.current)
        .then((data) => setRecentViewers(data.entries || []))
        .catch((err) => console.error("Failed to load access log:", err));
    } catch (error) {
      if (error.response?.status === 404 && version === "previous") {
        setRollbackMessage("No previous version available.");
        setOpenError(true);
      } else if (error.response?.status === 403) {
        setErrorMessage("Access Denied!!");
        setOpenError(true);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secretName, version, platformName, accessToken, fetchSecret, vaultId, awsProfile]);

  useEffect(() => {
    loadSecret();
  }, [loadSecret]);

  // Load version history — all three providers support it (AWS/Azure keep
  // full version history natively, not just AWSCURRENT/AWSPREVIOUS).
  const loadVersionHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const data = await fetchVersionHistory(secretName, platformName, headersRef.current);
      setVersionHistory(data.versions || []);
    } catch (err) {
      console.error("Failed to load version history:", err);
    } finally {
      setHistoryLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secretName, platformName, fetchVersionHistory]);

  const handleOpenHistory = () => {
    setHistoryOpen(true);
    loadVersionHistory();
  };

  // Delete secret — authorization is enforced by the backend (rbac.deleteAccessRoles),
  // not guessed client-side, so a 403 here shows the backend's actual reason.
  const handleDeleteSecret = useCallback(async () => {
    if (!window.confirm("Are you sure you want to delete this secret?")) return;
    try {
      const response = await deleteSecret(secretName, platformName, headersRef.current);
      if (response.status === 200) {
        navigate(`/secrets?selectedCluster=${selectedCluster}`);
      } else {
        setErrorMessage("Error deleting secret");
        setOpenError(true);
      }
    } catch (error) {
      console.error("Error deleting secret:", error);
      setErrorMessage(error.response?.data?.error || `Failed to delete "${secretName}".`);
      setOpenError(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secretName, platformName, vaultId, awsProfile, deleteSecret, navigate, selectedCluster]);

  const [vaultPathCopied, setVaultPathCopied] = useState(false);
  const handleCopyVaultPath = useCallback(() => {
    navigator.clipboard.writeText(secretName);
    setVaultPathCopied(true);
    setTimeout(() => setVaultPathCopied(false), 1500);
  }, [secretName]);

  // Edit secret
  const handleEditClick = useCallback(() => {
    const params = new URLSearchParams({ platformName, selectedCluster, vaultName: vaultId });
    if (platformName === config.platforms[0]) params.set("awsProfile", awsProfile);
    navigate(`/update-secret/${secretName}?${params.toString()}`);
  }, [navigate, secretName, platformName, selectedCluster, vaultId, awsProfile]);

  // Duplicate — opens a dialog so the target cluster/provider, environment,
  // and team can all be chosen (they don't have to match the source).
  const handleConfirmDuplicate = useCallback(
    async ({ newName, targetCluster, targetProvider, environment, team }) => {
      const targetProviderObj =
        getClusterProviders(targetCluster).find((p) => p.platformName === targetProvider)
        || { platformName: targetProvider, vaultId: "" };
      const targetHeaders = buildApiHeaders({
        accessToken,
        roles,
        platformName: targetProviderObj.platformName,
        vaultId: targetProviderObj.vaultId,
        awsProfile,
        userDisplayName,
      });

      await duplicateSecret({
        sourceName: secretName,
        sourcePlatformName: platformName,
        sourceHeaders: headersRef.current,
        newName,
        targetPlatformName: targetProviderObj.platformName,
        targetHeaders,
        environment,
        team,
        nameParts,
      });

      setDuplicateOpen(false);
      setDuplicateSuccess({ newName, targetCluster, targetProvider: targetProviderObj.platformName });
    },
    [secretName, platformName, duplicateSecret, accessToken, roles, awsProfile, userDisplayName, nameParts]
  );

  const handleViewDuplicate = useCallback(() => {
    if (!duplicateSuccess) return;
    const { newName, targetCluster, targetProvider } = duplicateSuccess;
    const targetProviderObj = getClusterProviders(targetCluster).find((p) => p.platformName === targetProvider);
    let vaultName = targetProviderObj?.vaultId || "";
    if (targetProvider === config.platforms[0] && config.awsProfiles[awsProfile]) {
      vaultName = config.awsProfiles[awsProfile].region;
    }
    const params = new URLSearchParams({ platformName: targetProvider, selectedCluster: targetCluster, vaultName });
    if (targetProvider === config.platforms[0]) params.set("awsProfile", awsProfile);
    setDuplicateSuccess(null);
    navigate(`/view-secret/${newName}?${params.toString()}`);
  }, [duplicateSuccess, navigate, awsProfile]);

  // Rename — emulated as create-under-new-name then delete-old-name (no backend rename endpoint)
  const handleConfirmRename = useCallback(
    async (newName) => {
      setRenameLoading(true);
      try {
        const result = await renameSecret({
          oldName: secretName,
          newName,
          platformName,
          headers: headersRef.current,
          nameParts,
        });
        setRenameOpen(false);
        if (result.status === "partial") {
          setRenamePartialMessage(
            `"${newName}" was created, but the old secret "${secretName}" could not be deleted. Please delete it manually.`
          );
          setRenamePartialOpen(true);
        } else {
          const params = new URLSearchParams({ platformName, selectedCluster, vaultName: vaultId });
          if (platformName === config.platforms[0]) params.set("awsProfile", awsProfile);
          navigate(`/view-secret/${newName}?${params.toString()}`);
        }
      } catch (error) {
        console.error("Error renaming secret:", error);
        setErrorMessage(error.response?.data?.error || `Failed to rename "${secretName}".`);
        setOpenError(true);
      } finally {
        setRenameLoading(false);
      }
    },
    [secretName, platformName, renameSecret, nameParts, navigate, selectedCluster, vaultId, awsProfile]
  );

  // Rollback (generic — previous version)
  const handleRollback = useCallback(async () => {
    setOpenWarning(false);
    setRollbackMessage("");
    try {
      const response = await rollbackSecret(secretName, platformName, headersRef.current);
      setRollbackMessage(response.data.message || "Successfully rolled back to the previous version.");
      setOpenSuccess(true);
    } catch (error) {
      console.error(error);
      setRollbackMessage("Failed to rollback the secret.");
      setOpenError(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secretName, platformName, vaultId, awsProfile, rollbackSecret]);

  // Rollback to a specific version from the history drawer
  const handleRollbackToVersion = useCallback(async (targetVersion) => {
    if (!window.confirm(`Roll back "${secretName}" to version ${targetVersion}?`)) return;
    setHistoryOpen(false);
    try {
      const response = await rollbackSecret(secretName, platformName, headersRef.current, targetVersion);
      setRollbackMessage(response.data.message || `Rolled back to version ${targetVersion}.`);
      setOpenSuccess(true);
    } catch (error) {
      console.error(error);
      setRollbackMessage(`Failed to rollback to version ${targetVersion}.`);
      setOpenError(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secretName, platformName, rollbackSecret]);

  const handleSuccessAlertClose = useCallback(() => {
    setOpenSuccess(false);
    setVersion("current");
    navigate(
      `/view-secret/${secretName}?platformName=${platformName}&selectedCluster=${selectedCluster}&vaultName=${vaultId}&awsProfile=${awsProfile}`
    );
  }, [navigate, secretName, platformName, selectedCluster, vaultId, awsProfile]);

  const handleDialogClose = useCallback(() => {
    setOpenError(false);
    setOpenSuccess(false);
    setOpenWarning(false);
    if (version === "previous") setVersion("current");
  }, [version]);

  return (
    <div>
      <div className="pl-8 mb-4">
        <Breadcrumbs />
      </div>

      <div className="flex flex-col items-center" style={{ minHeight: "100vh" }}>
        {loading && (
          <Loading
            open={loading}
            type="skeleton"
            variant="code"
            rows={32}
          />
        )}

        {secret && !loading && (
          <h1 className="font-heading text-2xl font-bold text-color-text-brand-primary-default" style={{ marginTop: 20, marginBottom: 20 }}>
            {secretName}
          </h1>
        )}

        {secret && !loading && platformName === "vault" && (
          <div className="flex items-center gap-2 mb-4 text-sm text-color-text-secondary">
            <span>
              Mount: <span className="font-mono">{vaultId}</span> · Path (use this in an ExternalSecret's{" "}
              <span className="font-mono">keys:</span>): <span className="font-mono">{secretName}</span>
            </span>
            <Tooltip>
              <TooltipTrigger asChild>
                <IconButton size="sm" variant="ghost" aria-label="Copy Vault path" onClick={handleCopyVaultPath}>
                  <CopyIcon className="size-4" />
                </IconButton>
              </TooltipTrigger>
              <TooltipContent>{vaultPathCopied ? "Copied!" : "Copy path"}</TooltipContent>
            </Tooltip>
          </div>
        )}

        {secret && !loading && (
          <SecretDetailsCard
            name={getTagValue(secretTag, "UpdatedBy")}
            timeAgo={<TimeFormatter timestamp={updatedOn} lowercase={true} />}
            clusterName={selectedCluster}
            secretName={secretName}
            env={getTagValue(secretTag, "Environment")}
            createdBy={getTagValue(secretTag, "CreatedBy")}
            project={getTagValue(secretTag, "Project")}
            store={vaultId}
            recentViewers={recentViewers}
          />
        )}

        <div className="w-full">
          <Dialogbox open={openSuccess} onClose={handleSuccessAlertClose} title="Success" severity="success"
            message={<div>{secretName} rollback successful!<br />Message: {rollbackMessage}</div>}
          />
          <Dialogbox open={openWarning} onClose={handleDialogClose} title="Warning" severity="warning"
            message={<div>Do you want to perform this action?<br />Click confirm to continue or cancel.</div>}
            onConfirm={handleRollback} confirmText="Confirm Rollback" closeText="Cancel"
          />
          <Dialogbox open={openError} onClose={handleDialogClose} title="Error" severity="error"
            message={<div>Message: {rollbackMessage || errorMessage}</div>}
          />

          <DuplicateSecretDialog
            open={duplicateOpen}
            onClose={() => setDuplicateOpen(false)}
            sourceName={secretName}
            defaultCluster={selectedCluster}
            defaultEnvironment={getTagValue(secretTag, "Environment")}
            defaultTeam={getTagValue(secretTag, "Project")}
            userRole={roles}
            onConfirm={handleConfirmDuplicate}
          />

          <Dialogbox
            open={!!duplicateSuccess}
            onClose={() => setDuplicateSuccess(null)}
            title="Secret duplicated"
            severity="success"
            message={<div>Created "{duplicateSuccess?.newName}" in cluster "{duplicateSuccess?.targetCluster}".</div>}
            onConfirm={handleViewDuplicate}
            confirmText="View duplicate"
            closeText="Stay here"
          />

          <RenameSecretDialog
            open={renameOpen}
            onClose={() => setRenameOpen(false)}
            currentName={secretName}
            onConfirm={handleConfirmRename}
            loading={renameLoading}
          />

          <Dialogbox
            open={renamePartialOpen}
            onClose={() => setRenamePartialOpen(false)}
            title="Rename partially completed"
            severity="warning"
            message={renamePartialMessage}
          />
        </div>

        {platformName === config.platforms[0] && (
          <AwsProfileSelector
            selectedProfile={awsProfile}
            onProfileChange={handleAwsProfileChange}
            platformName={platformName}
          />
        )}

        {secret && !loading && (
          <div className="rounded-lg bg-color-bg-surface flex flex-col justify-center items-center m-1" style={{ width: "60rem", height: "50rem" }}>
            <div className="flex justify-between items-center bg-color-bg-secondary border-b border-color-border-secondary" style={{ width: "40rem", height: "2.5rem" }}>
              <div className="flex items-center gap-2" style={{ marginLeft: 16 }}>
                {/* Current/Previous version chip selector */}
                <Chip
                  label="Current"
                  size="sm"
                  type="selectable"
                  selected={version === "current"}
                  onClick={() => setVersion("current")}
                />
                <Chip
                  label="Previous"
                  size="sm"
                  type="selectable"
                  selected={version === "previous"}
                  onClick={() => setVersion("previous")}
                />
              </div>

              <div className="flex items-center gap-1" style={{ marginRight: 16 }}>
                {/* Version history — all three providers */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <IconButton fill="Transparent" onClick={handleOpenHistory} icon={<ClockIcon className="size-4" />} aria-label="Version history" />
                  </TooltipTrigger>
                  <TooltipContent>Version history</TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <IconButton fill="Transparent" onClick={handleEditClick} icon={<PencilIcon className="size-4" />} aria-label="Edit" />
                  </TooltipTrigger>
                  <TooltipContent>Edit</TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <IconButton
                      fill="Transparent"
                      onClick={() => setDuplicateOpen(true)}
                      icon={<CopyIcon className="size-4" />}
                      aria-label="Duplicate"
                    />
                  </TooltipTrigger>
                  <TooltipContent>Duplicate</TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <IconButton fill="Transparent" onClick={() => setRenameOpen(true)} icon={<TagIcon className="size-4" />} aria-label="Rename" />
                  </TooltipTrigger>
                  <TooltipContent>Rename</TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <IconButton fill="Transparent" onClick={handleDeleteSecret} icon={<TrashIcon className="size-4 text-color-icon-state-error-default" />} aria-label="Delete" />
                  </TooltipTrigger>
                  <TooltipContent>Delete</TooltipContent>
                </Tooltip>
              </div>
            </div>

            {version === "previous" && (
              <Button
                variant="outlined"
                onClick={() => setOpenWarning(true)}
                disabled={loading}
                className="my-2"
              >
                {loading ? "Rolling Back..." : "Rollback to Previous Version"}
              </Button>
            )}

            <div className="rounded-lg my-2 p-1 bg-color-bg-secondary border border-color-border-secondary" style={{ overflow: "auto", width: "55rem", height: "40rem" }}>
              <CodeEditor language="json" value={secret} readOnly="true" />
            </div>
          </div>
        )}
      </div>

      {/* Version history drawer — Vault only */}
      <Sheet open={historyOpen} onOpenChange={setHistoryOpen}>
        <SheetContent side="right">
          <div className="flex flex-col h-full p-4" style={{ width: 340 }}>
            <SheetHeader>
              <SheetTitle className="font-heading text-lg font-bold text-color-text-brand-primary-default">
                Version History
              </SheetTitle>
            </SheetHeader>
            <p className="text-xs text-color-text-secondary mb-1">
              {secretName} — click any version to roll back
            </p>
            <hr className="border-color-border-secondary mb-4" />

            {historyLoading ? (
              <div className="flex justify-center mt-8">
                <Loader type="brand" />
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto">
                {versionHistory.map((v) => (
                  <div
                    key={v.version}
                    className={`mb-3 p-3 rounded-lg border flex flex-col ${
                      v.is_current
                        ? "border-color-border-brand-primary-default bg-color-bg-brand-tertiary-default"
                        : "border-color-border-secondary bg-color-bg-surface"
                    }`}
                  >
                    <div className="flex justify-between w-full mb-1">
                      <span className="text-sm font-bold">Version {formatVersionLabel(v.version)}</span>
                      {v.is_current && <Badge color="purple" size="sm">CURRENT</Badge>}
                    </div>

                    {v.updated_by && (
                      <div className="flex items-center gap-1 mb-1">
                        <span className="text-xs text-color-text-secondary">{v.updated_by}</span>
                      </div>
                    )}

                    <span className="text-xs text-color-text-secondary">
                      {v.created_at ? new Date(v.created_at).toLocaleString() : "—"}
                    </span>

                    {!v.is_current && !v.deleted && (
                      <Button
                        size="sm"
                        variant="outlined"
                        fullWidth
                        className="mt-2"
                        onClick={() => handleRollbackToVersion(v.version)}
                      >
                        ↩ Roll back to v{formatVersionLabel(v.version)}
                      </Button>
                    )}
                    {v.is_current && (
                      <span className="text-xs text-color-text-secondary mt-1">
                        This is the current version
                      </span>
                    )}
                  </div>
                ))}

                {versionHistory.length === 0 && (
                  <p className="text-sm text-color-text-secondary text-center mt-8">
                    No version history available.
                  </p>
                )}
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

export default ViewSecret;
